import Phaser from 'phaser'

import { ENV_TEX, type Walkways } from '~/game/environment'
import type { LoungeLayout } from '~/game/world'

// The office cat: wanders the corridors, sits where it pleases, naps on the
// lounge rug. Purely decorative — never touches the EventBus, never blocks a
// seat, dies with the scene (scene.time / tweens only). Click it for a mew.

export function startOfficeCat(
  scene: Phaser.Scene,
  deps: { walkways: Walkways; lounge: LoungeLayout },
): () => void {
  const { walkways, lounge } = deps
  const streets = walkways.streets
  if (streets.length === 0) return () => undefined

  let stopped = false
  const timers: Phaser.Time.TimerEvent[] = []
  let walkTween: Phaser.Tweens.Tween | undefined

  const spot = (): { x: number; y: number } => {
    // Mostly a random point on a random street; sometimes the lounge rug.
    if (Math.random() < 0.22) {
      return {
        x: lounge.cx + Phaser.Math.Between(-lounge.w / 3, lounge.w / 3),
        y: lounge.cy + Phaser.Math.Between(-6, 10),
      }
    }
    const s = streets[Math.floor(Math.random() * streets.length)]
    return {
      x: Phaser.Math.Between(s.x + 10, s.x + Math.max(11, s.w - 10)),
      y: Phaser.Math.Between(s.y + 3, s.y + Math.max(4, s.h - 3)),
    }
  }

  const start = spot()
  const cat = scene.add
    .image(start.x, start.y, ENV_TEX.catStand)
    .setOrigin(0.5, 1)
    .setScale(1.8)
    .setDepth(9)
  cat.setInteractive({ useHandCursor: true })
  cat.setData('consumesClick', true)
  cat.on('pointerup', () => {
    const mew = scene.add
      .text(cat.x, cat.y - 18, 'mew', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '9px',
        fontStyle: 'italic',
        color: '#e8c8a0',
      })
      .setOrigin(0.5, 1)
      .setDepth(30)
    scene.tweens.add({
      targets: mew,
      y: mew.y - 10,
      alpha: 0,
      duration: 1200,
      onComplete: () => mew.destroy(),
    })
  })

  const later = (ms: number, fn: () => void): void => {
    timers.push(scene.time.delayedCall(ms, fn))
  }

  const onLounge = (): boolean =>
    Math.abs(cat.x - lounge.cx) < lounge.w / 2 &&
    Math.abs(cat.y - lounge.cy) < 26

  let zzz: Phaser.GameObjects.Text | undefined
  const wake = (): void => {
    zzz?.destroy()
    zzz = undefined
  }

  const wander = (): void => {
    if (stopped) return
    wake()
    const to = spot()
    const dist = Phaser.Math.Distance.Between(cat.x, cat.y, to.x, to.y)
    cat.setTexture(ENV_TEX.catStand)
    cat.setFlipX(to.x < cat.x)
    walkTween = scene.tweens.add({
      targets: cat,
      x: to.x,
      y: to.y,
      duration: Math.max(900, (dist / 26) * 1000),
      ease: 'Sine.inOut',
      onComplete: () => {
        if (stopped) return
        // Arrived: nap on the rug, or sit a while, or wander on.
        if (onLounge()) {
          cat.setTexture(ENV_TEX.catSit)
          zzz = scene.add
            .text(cat.x + 8, cat.y - 16, 'z', {
              fontFamily: 'system-ui, sans-serif',
              fontSize: '9px',
              color: '#8b93a7',
            })
            .setDepth(9)
          later(Phaser.Math.Between(10000, 18000), wander)
        } else if (Math.random() < 0.45) {
          cat.setTexture(ENV_TEX.catSit)
          later(Phaser.Math.Between(5000, 9000), wander)
        } else {
          later(Phaser.Math.Between(1200, 3200), wander)
        }
      },
    })
  }
  later(4000, wander)

  return () => {
    if (stopped) return
    stopped = true
    walkTween?.remove()
    for (const t of timers) t.remove(false)
    wake()
    cat.destroy()
  }
}
